import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/da2j7dbd3r93mn1k39sg";

export default function Contact() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐校验：命中则不提交并给出与成功一致的反馈
    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setFormStatus("success");
      form.reset();
      return;
    }

    setFormStatus("submitting");
    setFormError("");

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        const k = key as string;
        if (k === "contact_alt") return;
        payload.append(k, String(value));
      });

      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      const spamHit =
        serverMsg.toLowerCase().includes("spam") || serverMsg.includes("form data is spam");

      if (!res.ok || !parsed || parsed.code !== "OK" || spamHit) {
        setFormStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
        return;
      }

      setFormStatus("success");
      form.reset();
    } catch {
      setFormStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  }

  return (
    <section id="contact" className="bg-foreground-950 py-14 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 md:gap-12">
          {/* 左侧：联系信息 */}
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-bold text-primary-400">
              <i className="ri-mail-send-line"></i> 联系我们
            </span>
            <h2 className="mt-2 font-heading text-2xl font-black text-background-50 md:text-4xl">
              加入我们，一起追更
            </h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-background-100 md:text-base">
              无论是想反馈建议、咨询追更服务，还是想投稿自己的同人创作，
              欢迎随时通过表单联系。你的每一份声音，都是我们前进的动力。
            </p>

            <div className="mt-8 space-y-4">
              {[
                { icon: "ri-mail-line", label: "邮箱", value: "contact@naruto-fan.example" },
                { icon: "ri-question-answer-line", label: "在线支持", value: "工作日 9:00 - 21:00" },
                { icon: "ri-community-line", label: "创作者投稿", value: "投稿作品请附简介与样章" },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-500/15 text-xl text-primary-400">
                    <i className={c.icon}></i>
                  </span>
                  <div>
                    <p className="text-xs text-background-200">{c.label}</p>
                    <p className="text-sm font-bold text-background-50">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-background-100/15 bg-background-50/5 p-4">
              <p className="text-xs leading-relaxed text-background-200">
                <i className="ri-flag-line mr-1 text-primary-400"></i>
                新用户注册即送 7 天免费阅读券，热门连载抢先看，追更不迷路。
              </p>
            </div>
          </div>

          {/* 右侧：联系表单 */}
          <div className="rounded-xl border border-background-100/15 bg-background-50 p-6 md:p-8">
            <h3 className="font-heading text-lg font-black text-foreground-900">给我们留言</h3>
            <p className="mt-1 text-sm text-foreground-600">
              填写以下信息，我们会尽快与你联系。
            </p>

            <form
              id="contact-form"
              data-readdy-form
              className="mt-6 space-y-4"
              onSubmit={handleSubmit}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-name" className="mb-1.5 block text-xs font-bold text-foreground-700">
                    姓名
                  </label>
                  <input
                    id="cf-name"
                    name="name"
                    type="text"
                    required
                    placeholder="你的称呼"
                    className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className="mb-1.5 block text-xs font-bold text-foreground-700">
                    邮箱
                  </label>
                  <input
                    id="cf-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="cf-topic" className="mb-1.5 block text-xs font-bold text-foreground-700">
                  留言主题
                </label>
                <select
                  id="cf-topic"
                  name="topic"
                  required
                  defaultValue=""
                  className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                >
                  <option value="" disabled>
                    请选择留言类型
                  </option>
                  <option value="反馈建议">反馈建议</option>
                  <option value="追更咨询">追更咨询</option>
                  <option value="创作者投稿">创作者投稿</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="cf-message" className="mb-1.5 block text-xs font-bold text-foreground-700">
                  留言内容
                </label>
                <textarea
                  id="cf-message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  placeholder="请在这里写下你的留言或投稿简介…"
                  className="w-full resize-none rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                ></textarea>
              </div>

              {/* 反垃圾蜜罐字段 */}
              <div className="hp-guard" aria-hidden="true">
                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === "submitting"}
                className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {formStatus === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin"></i> 正在提交…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill mr-2"></i> 提交留言
                  </>
                )}
              </button>

              {formStatus === "success" && (
                <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm font-medium text-secondary-800">
                  <i className="ri-checkbox-circle-fill"></i> 提交成功！我们会尽快与你联系。
                </p>
              )}
              {formStatus === "error" && (
                <p className="flex items-center gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm font-medium text-primary-800">
                  <i className="ri-error-warning-fill"></i> {formError || "提交失败，请稍后再试。"}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}